import { useMemo, useState } from 'react'
import { Brain, Building2, TrendingUp, Euro, Clock, BedDouble, AlertTriangle, List } from 'lucide-react'
import AppLayout from '../AppLayout'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { RSA_LIST, type Rsa } from '../../data/rsa'
import { RsaDialog } from '../StrutturaDetailDialog'
import RsaDialogList from './RsaDialogList'
import type { AmbitoId } from '../../data/ambitiStats'

export default function RsaPage() {
  return (
    <AppLayout>
      {({ selectedAmbito, onSelectedAmbitoChange }) => (
        <RsaContent
          selectedAmbito={selectedAmbito}
          onSelectedAmbitoChange={onSelectedAmbitoChange}
        />
      )}
    </AppLayout>
  )
}

function RsaContent({
  selectedAmbito,
  onSelectedAmbitoChange,
}: {
  selectedAmbito: AmbitoId | null
  onSelectedAmbitoChange: (id: AmbitoId | null) => void
}) {
  const filtered = useMemo(
    () => selectedAmbito ? RSA_LIST.filter(r => r.ambitoId === selectedAmbito) : RSA_LIST,
    [selectedAmbito],
  )

  const totalRsa = filtered.length
  const totalAlzheimer = filtered.filter(r => r.nucleoAlzheimer).length
  const [selectedRsa, setSelectedRsa] = useState<Rsa | null>(null)

  // ── Statistiche seconda riga ──────────────────────────────────────────────
  const stats = useMemo(() => {
    const totListaOrd = filtered.reduce((s, r) => s + (r.listaAttesaOrdinaria ?? 0), 0)
    const totListaAlz = filtered.reduce((s, r) => s + (r.listaAttesaAlzheimer ?? 0), 0)

    const withRettaMin = filtered.filter(r => r.rettaMinNum !== null)
    const avgRettaMin = withRettaMin.length
      ? withRettaMin.reduce((s, r) => s + r.rettaMinNum!, 0) / withRettaMin.length
      : null
    const minRetta = withRettaMin.length ? Math.min(...withRettaMin.map(r => r.rettaMinNum!)) : null
    const maxRetta = filtered.filter(r => r.rettaMaxNum !== null).length
      ? Math.max(...filtered.filter(r => r.rettaMaxNum !== null).map(r => r.rettaMaxNum!))
      : null

    const conPosti = filtered.filter(r => (r.postiDisponibiliOrdinaria ?? 0) > 0)

    const totPosti = filtered.reduce((s, r) => s + (r.totalePostiOrdinaria ?? 0), 0)
    const totPostiDisp = filtered.reduce((s, r) => s + (r.postiDisponibiliOrdinaria ?? 0), 0)
    const tassoOccupazione = totPosti > 0
      ? Math.round(((totPosti - totPostiDisp) / totPosti) * 100)
      : null

    const byListaOrd = [...filtered]
      .filter(r => r.listaAttesaOrdinaria > 0)
      .sort((a, b) => b.listaAttesaOrdinaria - a.listaAttesaOrdinaria)

    const byRetta = [...filtered]
      .filter(r => r.rettaMinNum !== null)
      .sort((a, b) => a.rettaMinNum! - b.rettaMinNum!)

    return { totListaOrd, totListaAlz, avgRettaMin, minRetta, maxRetta, conPosti, byListaOrd, byRetta, totPosti, totPostiDisp, tassoOccupazione }
  }, [filtered])

  return (
    <section className="telemetry-dashboard">
      <div className="telemetry-inner">
        <h3 className="telemetry-section-title" style={{ marginBottom: 0 }}>
          {selectedAmbito
            ? `Ambito di ${selectedAmbito.charAt(0).toUpperCase() + selectedAmbito.slice(1)} - Residenze Sanitarie Assistenziali`
            : 'Provincia di Monza e Brianza - Residenze Sanitarie Assistenziali'}
        </h3>

        <AutoGrid>

          {/* Card 1 — Cos'è una RSA */}
          <BSDCard kind="panel" sezione="Residenze Sanitarie Assistenziali" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <p style={{ color: 'var(--text-1)', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
              Le <strong>Residenze Sanitarie Assistenziali (RSA)</strong> sono strutture
              socio-sanitarie residenziali destinate ad anziani non autosufficienti.
              Garantiscono assistenza medica, infermieristica, riabilitativa e di supporto 24h su 24.
              L'accesso avviene tramite valutazione dell'<strong>Unità di Valutazione
              Multidimensionale (UVM)</strong> dell'ASST di riferimento.
            </p>
          </BSDCard>

          {/* Card 2 — Mappa ambiti */}
          <BSDCard
            className="telemetry-map-card"
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            style={{ minHeight: 120, padding: 10 }}
          >
            <DistrictMap
              onSelectDistrict={(id) => onSelectedAmbitoChange(id as AmbitoId)}
              onClearSelection={() => onSelectedAmbitoChange(null)}
              selectedDistrictId={selectedAmbito}
            />
          </BSDCard>

          {/* Card 3 — Totale RSA */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="RSA"
            value={String(totalRsa)}
            icon={Building2}
            color="#f59e0b"
            zoomable
            hideZoomButton
            sub={
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                Tutte le Residenze Sanitarie Assistenziali nel territorio selezionato.
              </p>
            }
            zoomSub={<RsaDialogList rsa={filtered} color="#f59e0b" />}
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '6px 0',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid var(--border)',
                  cursor: 'pointer',
                  justifyContent: 'center',
                  marginTop: 8,
                  paddingTop: 8,
                }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />

          {/* Card 4 — RSA con Nucleo Alzheimer */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="Con Nucleo Alzheimer"
            value={String(totalAlzheimer)}
            icon={Brain}
            color="#8b5cf6"
            zoomable
            hideZoomButton
            sub={
              <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                RSA dotate di nucleo specializzato per pazienti con demenza di Alzheimer.
              </p>
            }
            zoomSub={<RsaDialogList rsa={filtered.filter(r => r.nucleoAlzheimer)} color="#8b5cf6" />}
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '6px 0',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  borderTop: '1px solid var(--border)',
                  cursor: 'pointer',
                  justifyContent: 'center',
                  marginTop: 8,
                  paddingTop: 8,
                }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />

          {/* Card 5 — Occupazione */}
          <BSDCard kind="panel" sezione="Residenze Sanitarie Assistenziali" style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <TrendingUp size={14} style={{ color: '#f59e0b' }} />
              Occupazione
            </h3>
            {stats.tassoOccupazione !== null ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: '#f59e0b', lineHeight: 1 }}>
                    {stats.tassoOccupazione}%
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>occupato</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-2)', overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ height: '100%', width: `${stats.tassoOccupazione}%`, background: '#f59e0b', borderRadius: 4, transition: 'width 0.4s' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)' }}>
                  <span>{stats.totPosti - stats.totPostiDisp} occupati</span>
                  <span>{stats.totPostiDisp} liberi</span>
                </div>
              </>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-3)', margin: 0 }}>Dati non disponibili.</p>
            )}
          </BSDCard>

        </AutoGrid>

        {/* ── Seconda riga: tempi di attesa e costi ── */}
        <AutoGrid style={{ marginTop: 14 }}>

          {/* Card 6 — Totale lista attesa ordinaria */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="In lista d'attesa (ordinaria)"
            value={stats.totListaOrd.toLocaleString('it-IT')}
            icon={Clock}
            color="#f59e0b"
            sub={
              <div style={{ display: 'grid', gap: 4 }}>
                {stats.byListaOrd.map(r => (
                  <div
                    key={r.id}
                    onClick={() => setSelectedRsa(r)}
                    style={{
                      fontSize: 11,
                      color: 'var(--text-2)',
                      borderLeft: '2px solid #f59e0b',
                      paddingLeft: 6,
                      lineHeight: 1.4,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-1)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nome}</span>
                      <span style={{ display: 'block', color: 'var(--text-3)', fontSize: 10 }}>{r.localita}</span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', flexShrink: 0 }}>{r.listaAttesaOrdinaria}</span>
                  </div>
                ))}
              </div>
            }
          />

          {/* Card 7 — Totale lista attesa Alzheimer */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="In lista d'attesa (Alzheimer)"
            value={stats.totListaAlz.toLocaleString('it-IT')}
            icon={Brain}
            color="#8b5cf6"
            sub={
              <div style={{ display: 'grid', gap: 4 }}>
                {filtered.filter(r => r.listaAttesaAlzheimer > 0)
                  .sort((a, b) => b.listaAttesaAlzheimer - a.listaAttesaAlzheimer)
                  .map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <span
                        onClick={() => setSelectedRsa(r)}
                        style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >{r.nome}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', flexShrink: 0 }}>{r.listaAttesaAlzheimer}</span>
                    </div>
                  ))}
              </div>
            }
          />

          {/* Card 8 — Retta media giornaliera */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="Retta media giornaliera"
            value={stats.avgRettaMin !== null ? `€ ${stats.avgRettaMin.toFixed(2).replace('.', ',')}` : '—'}
            icon={Euro}
            color="#10b981"
            sub={
              <div style={{ display: 'grid', gap: 4 }}>
                {stats.byRetta.map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span
                      onClick={() => setSelectedRsa(r)}
                      style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >{r.nome}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#10b981', flexShrink: 0 }}>
                      {r.rettaMin}
                    </span>
                  </div>
                ))}
              </div>
            }
          />

          {/* Card 9 — Range rette (min/max provincia) */}
          <BSDCard kind="panel" sezione="Residenze Sanitarie Assistenziali" style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 10 }}>Range rette giornaliere</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Retta minima</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>
                  {stats.minRetta !== null ? `€ ${stats.minRetta.toFixed(2).replace('.', ',')}` : '—'}
                </span>
              </div>
              <div style={{ height: 1, background: 'var(--border)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Retta massima</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#ef4444' }}>
                  {stats.maxRetta !== null ? `€ ${stats.maxRetta.toFixed(2).replace('.', ',')}` : '—'}
                </span>
              </div>
              <div style={{ height: 1, background: 'var(--border)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Costo mensile stimato</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
                  {stats.minRetta !== null && stats.maxRetta !== null
                    ? `€ ${(stats.minRetta * 30).toFixed(0)} – € ${(stats.maxRetta * 30).toFixed(0)}`
                    : '—'}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 'auto' }}>
                * Retta ordinaria, esclusi eventuali costi aggiuntivi
              </div>
            </div>
          </BSDCard>

          {/* Card 10 — RSA con posti disponibili */}
          <BSDCard
            kind="kpi"
            sezione="Residenze Sanitarie Assistenziali"
            label="RSA con posti liberi"
            value={String(stats.conPosti.length)}
            icon={stats.conPosti.length > 0 ? BedDouble : AlertTriangle}
            color={stats.conPosti.length > 0 ? '#0ea5e9' : '#f59e0b'}
            sub={
              stats.conPosti.length > 0 ? (
                <div style={{ display: 'grid', gap: 4 }}>
                  {stats.conPosti.map(r => (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <span
                        onClick={() => setSelectedRsa(r)}
                        style={{ fontSize: 10, color: 'var(--text-2)', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >{r.nome}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9', flexShrink: 0 }}>
                        {r.postiDisponibiliOrdinaria} posti
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: 11, color: 'var(--text-3)', margin: 0 }}>
                  Nessun posto disponibile nelle RSA{selectedAmbito ? " dell'ambito selezionato" : " della provincia"}.
                </p>
              )
            }
          />

        </AutoGrid>

        <RsaDialog rsa={selectedRsa} onClose={() => setSelectedRsa(null)} fromUrl="/rsa" />
      </div>
    </section>
  )
}
